import React, { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../contexts/LocaleContext';
import { LanguageSelector } from '../LanguageSelector';
import type { AuthError } from '../../types/auth';
import styles from './LoginPage.module.css';

interface LoginPageProps {
  onSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { signIn } = useAuth();
  const { selectedLocale } = useLocale();

  // Translation helper for dynamic content
  const t = (en: string, ru: string) => {
    return selectedLocale === 'ru' ? ru : en;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signIn(username, password);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const authError = err as AuthError;

      // Display error based on status
      if (authError.status === 401) {
        setError(t('Invalid username or password. Please try again.', 'Неверный логин или пароль. Попробуйте снова.'));
      } else if (authError.status === 500) {
        setError(t('Server error. Please try again later.', 'Ошибка сервера. Попробуйте позже.'));
      } else {
        setError(authError.message || t('Login failed. Please try again.', 'Ошибка входа. Попробуйте снова.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.login_container}>
      <div className={styles.login_card}>
        {/* Three-column header: Language Selector, Logo, Spacer */}
        <div className={styles.card_header}>
          <div className={styles.header_left}>
            <LanguageSelector />
          </div>
          <div className={styles.header_center}>
            <img src="/diana_logo_monochrome.png" alt="Diana Lapteva" className={styles.logo} />
          </div>
          <div className={styles.header_right}>
            {/* Spacer for symmetry */}
          </div>
        </div>

        {/* Title section - Bilingual (both languages displayed) */}
        <div className={styles.login_header}>
          <h1 className={styles.login_title}>
            Commercial Request
            <br />
            <span className={styles.title_ru}>Коммерческий запрос</span>
          </h1>
          <p className={styles.login_subtitle}>
            {t(
              'Please sign in to access your project summary',
              'Пожалуйста, войдите, чтобы получить доступ к резюме проекта'
            )}
          </p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className={styles.login_form}>
          <div className={styles.form_group}>
            <label htmlFor="username" className={styles.form_label}>
              {t('Username', 'Логин')}
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.form_input}
              placeholder={t('Enter your username', 'Введите ваш логин')}
              autoComplete="username"
              autoFocus
              required
              disabled={isLoading}
              aria-describedby={error ? 'error-message' : undefined}
            />
          </div>

          <div className={styles.form_group}>
            <label htmlFor="password" className={styles.form_label}>
              {t('Password', 'Пароль')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.form_input}
              placeholder={t('Enter your password', 'Введите ваш пароль')}
              autoComplete="current-password"
              required
              disabled={isLoading}
              aria-describedby={error ? 'error-message' : undefined}
            />
          </div>

          {/* Error message */}
          {error && (
            <div id="error-message" className={styles.error_message} role="alert">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            className={styles.submit_button}
            disabled={!username.trim() || !password.trim() || isLoading}
          >
            {isLoading ? (
              <span className={styles.loading}>
                <span className={styles.spinner} aria-hidden="true"></span>
                {t('Loading...', 'Загрузка...')}
              </span>
            ) : (
              t('ACCESS DOCUMENT', 'ДОСТУП К ДОКУМЕНТУ')
            )}
          </button>
        </form>

        {/* Footer */}
        <div className={styles.login_footer}>
          <div className={styles.footer_divider}></div>
          <p className={styles.footer_text}>
            {t(
              'This document contains confidential project information',
              'Этот документ содержит конфиденциальную информацию о проекте'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
